// PesticideLog — Pest Control operator application ledger.
// State + federal law requires PCOs to record every treatment: applicator
// cert, EPA reg #, product, target pest, quantity, dilution, weather.
// One tap CSV export for state inspector visits.
import { useEffect, useState } from "react";
import { api, API, formatApiErrorDetail } from "@/lib/api";
import { AdminShell } from "@/components/Layout";
import { toast } from "sonner";
import PesticideAutocomplete from "@/components/pest/PesticideAutocomplete";
import { PEST_COMMON_FIXES, protocolsForPest } from "@/data/pestCommonFixes";
import { PESTICIDES } from "@/data/pesticides";
import { Wrench } from "@phosphor-icons/react";
import { Bug, FloppyDisk, DownloadSimple, Trash, Plus, Sparkle, X, Camera } from "@phosphor-icons/react";

import { todayLocalISO as todayISO } from "@/lib/dateHelpers";
const nowHM = () => new Date().toTimeString().slice(0, 5);

const initialForm = {
  entry_date: todayISO(),
  entry_time: nowHM(),
  applicator_name: "",
  applicator_cert_number: "",
  applicator_cert_category: "7A – General Pest",
  product_name: "",
  epa_reg_number: "",
  target_pest: "German Cockroach",
  application_type: "crack_and_crevice",
  quantity: "",
  dilution: "",
  site_address: "",
  area_treated: "",
  temperature_f: "",
  wind_mph: "",
  weather_notes: "",
  reentry_hours: "",
  notes: "",
  // Auto-schedule follow-up wiring — filled when a Common Protocol is picked
  recurring_account_id: "",
  follow_up_days: null,
};

const APP_TYPE_LABELS = {
  crack_and_crevice: "Crack & Crevice",
  spot: "Spot Treatment",
  perimeter: "Perimeter",
  broadcast: "Broadcast",
  bait: "Bait Placement",
  fumigation: "Fumigation",
  granular: "Granular",
  aerosol: "Aerosol",
  dust: "Dust",
};

export default function PesticideLog() {
  const [data, setData] = useState(null);
  const [form, setForm] = useState(initialForm);
  const [saving, setSaving] = useState(false);
  const [showForm, setShowForm] = useState(false);
  // AI advisor state
  const [aiOpen, setAiOpen] = useState(false);
  const [aiInput, setAiInput] = useState({ target_pest: "German Cockroach", site_type: "residential", situation: "", has_kids_or_pets: false });
  const [aiLoading, setAiLoading] = useState(false);
  const [aiAdvice, setAiAdvice] = useState(null);
  // Photo-to-Pest ID state
  const [idLoading, setIdLoading] = useState(false);
  const [idResult, setIdResult] = useState(null);
  const [idPreview, setIdPreview] = useState(null);
  // Optional link to a recurring account so photos go into the journal
  const [accounts, setAccounts] = useState([]);
  const [linkAccountId, setLinkAccountId] = useState("");
  const [autoLinked, setAutoLinked] = useState(false);
  useEffect(() => {
    api.get("/pest/recurring-accounts").then(r => setAccounts(r.data.accounts || [])).catch(() => {});
    // Auto-Link: if the tech tapped "Start Job" on Route Radar, pre-select
    // that account here so photos flow into the right journal automatically.
    try {
      const active = JSON.parse(localStorage.getItem("pest_active_job") || "null");
      if (active?.id) {
        setLinkAccountId(active.id);
        setAutoLinked(true);
      }
    } catch { /* noop */ }
  }, []);

  const load = async () => {
    const r = await api.get("/pest/pesticide-log");
    setData(r.data);
  };
  useEffect(() => { load(); }, []);

  const setF = (k, v) => setForm(prev => ({ ...prev, [k]: v }));

  const save = async () => {
    if (!form.applicator_name || !form.product_name || !form.epa_reg_number ||
        !form.quantity || !form.site_address) {
      toast.error("Applicator, product, EPA reg #, quantity and site are required");
      return;
    }
    setSaving(true);
    try {
      const { data } = await api.post("/pest/pesticide-log", {
        ...form,
        temperature_f: form.temperature_f ? parseFloat(form.temperature_f) : null,
        wind_mph: form.wind_mph ? parseFloat(form.wind_mph) : null,
        reentry_hours: form.reentry_hours ? parseFloat(form.reentry_hours) : null,
        recurring_account_id: linkAccountId || form.recurring_account_id || null,
        follow_up_days: form.follow_up_days || null,
      });
      if (data?.scheduled) {
        toast.success(`Application logged · Next visit auto-booked ${data.scheduled.next_service_date}`);
      } else {
        toast.success("Application logged");
      }
      // Keep applicator + cert prefilled for the next entry
      setForm({ ...initialForm, entry_time: nowHM(),
                applicator_name: form.applicator_name,
                applicator_cert_number: form.applicator_cert_number,
                applicator_cert_category: form.applicator_cert_category });
      setShowForm(false);
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail ?? "Save failed"));
    } finally { setSaving(false); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this application? State law requires 2-year retention.")) return;
    await api.delete(`/pest/pesticide-log/${id}`);
    await load();
  };

  const exportCSV = () => {
    const token = localStorage.getItem("access_token");
    window.open(`${API}/pest/pesticide-log/export.csv?auth=${encodeURIComponent(token || "")}`, "_blank");
  };

  const openAI = () => {
    setAiAdvice(null);
    setAiInput({
      target_pest: form.target_pest || "German Cockroach",
      site_type: aiInput.site_type || "residential",
      situation: form.notes || "",
      has_kids_or_pets: false,
    });
    setAiOpen(true);
  };

  const askAI = async () => {
    setAiLoading(true);
    setAiAdvice(null);
    try {
      const r = await api.post("/pest/ai-treatment-advice", aiInput);
      setAiAdvice(r.data.advice);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail ?? "AI advisor failed — try again"));
    } finally { setAiLoading(false); }
  };

  const applyAdvice = () => {
    if (!aiAdvice) return;
    // Pre-fill the entry form with the AI's picks
    const first = (aiAdvice.products || [])[0] || {};
    setForm(prev => ({
      ...prev,
      target_pest: aiInput.target_pest,
      product_name: prev.product_name || first.name || "",
      epa_reg_number: prev.epa_reg_number || first.epa_reg || "",
      application_type: aiAdvice.application_type || prev.application_type,
      area_treated: prev.area_treated || (aiAdvice.placement || []).slice(0, 2).join(" · "),
      notes: prev.notes || `AI plan: ${aiAdvice.summary || ""}`.trim(),
    }));
    setAiOpen(false);
    setShowForm(true);
    toast.success("Applied AI plan — verify against product label before spraying");
  };

  const identifyPest = async (file) => {
    if (!file) return;
    setIdLoading(true);
    setIdResult(null);
    setIdPreview(URL.createObjectURL(file));
    try {
      const fd = new FormData();
      fd.append("file", file);
      const url = linkAccountId
        ? `/pest/ai-identify?account_id=${encodeURIComponent(linkAccountId)}`
        : "/pest/ai-identify";
      const r = await api.post(url, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setIdResult(r.data.identification);
      if (r.data.journal_saved) toast.success("Saved to account journal");
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail ?? "ID failed — try a clearer photo"));
    } finally { setIdLoading(false); }
  };

  const useIdForTreatment = () => {
    if (!idResult) return;
    // Open the AI advisor pre-filled with the identified pest
    setAiInput({
      target_pest: idResult.pest || "Other",
      site_type: aiInput.site_type || "residential",
      situation: `Photo ID (${idResult.confidence}): ${(idResult.observed || []).join(", ")}. ${idResult.next_step || ""}`,
      has_kids_or_pets: false,
    });
    setIdResult(null);
    setIdPreview(null);
    setAiAdvice(null);
    setAiOpen(true);
  };

  if (!data) return <div className="p-6 text-slate-500">Loading log…</div>;

  return (
    <AdminShell>
    <div className="p-4 md:p-6" data-testid="pesticide-log-page">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 mb-4">
        <div className="flex items-center gap-3">
          <div className="h-11 w-11 rounded-xl bg-[#0f172a] text-white flex items-center justify-center">
            <Bug weight="fill" size={22} className="text-emerald-400"/>
          </div>
          <div>
            <h1 className="font-display font-black text-3xl leading-none">Pesticide Log</h1>
            <div className="text-xs uppercase font-black tracking-widest text-slate-500 mt-1">
              State applicator compliance · <span className="text-red-500">2-year retention</span>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <label data-testid="pest-photo-id-label"
            className="h-11 px-4 bg-emerald-600 hover:bg-emerald-700 text-white font-black uppercase tracking-widest text-xs rounded-xl inline-flex items-center gap-2 shadow-lg cursor-pointer">
            <Camera weight="fill" size={16}/> {idLoading ? "Identifying…" : "ID Pest Photo"}
            <input type="file" accept="image/*" className="hidden"
              data-testid="pest-photo-id-input"
              onChange={e => { const f = e.target.files?.[0]; if (f) identifyPest(f); e.target.value = ""; }}/>
          </label>
          <button onClick={openAI} data-testid="pest-ai-btn"
            className="h-11 px-4 bg-fuchsia-600 hover:bg-fuchsia-700 text-white font-black uppercase tracking-widest text-xs rounded-xl inline-flex items-center gap-2 shadow-lg">
            <Sparkle weight="fill" size={16}/> Ask AI
          </button>
          <button onClick={exportCSV} data-testid="pest-export-csv"
            className="h-11 px-4 border-2 border-slate-300 hover:bg-slate-100 text-slate-700 font-black uppercase tracking-widest text-xs rounded-xl inline-flex items-center gap-2">
            <DownloadSimple weight="bold" size={16}/> Export CSV
          </button>
          <button onClick={() => setShowForm(v => !v)} data-testid="pest-add-btn"
            className="h-11 px-5 bg-[#00C4CC] hover:bg-[#00a9b1] text-white font-black uppercase tracking-widest text-xs rounded-xl inline-flex items-center gap-2 shadow-lg">
            <Plus weight="bold" size={16}/> {showForm ? "Cancel" : "New Application"}
          </button>
        </div>
      </div>

      {accounts.length > 0 && (
        <div className="mb-4 flex items-center gap-2 flex-wrap" data-testid="pest-link-account-row">
          {autoLinked && (
            <span className="text-[10px] font-black uppercase tracking-widest bg-fuchsia-600 text-white px-2 py-1 rounded" data-testid="pest-autolinked-badge">
              ✨ Auto-Linked
            </span>
          )}
          <label className="text-[10px] font-black uppercase tracking-widest text-slate-500">Link photos to account:</label>
          <select value={linkAccountId} onChange={e => { setLinkAccountId(e.target.value); setAutoLinked(false); }}
            data-testid="pest-link-account-select"
            className="h-9 px-3 border-2 border-slate-200 focus:border-[#00C4CC] outline-none rounded-lg text-xs font-medium">
            <option value="">— No account (still ID's pest)</option>
            {accounts.map(a => <option key={a.id} value={a.id}>{a.account_name} · {a.site_address}</option>)}
          </select>
          {linkAccountId && !autoLinked && <span className="text-[10px] font-black uppercase tracking-widest text-emerald-600">✓ Photos will save to journal</span>}
          {autoLinked && <span className="text-[10px] font-black uppercase tracking-widest text-fuchsia-600">From Route Radar · Start Job</span>}
        </div>
      )}

      {/* Pest frequency */}
      {Object.keys(data.pest_counts || {}).length > 0 && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-4" data-testid="pest-counts">
          {Object.entries(data.pest_counts).sort((a,b) => b[1]-a[1]).slice(0, 8).map(([p, n]) => (
            <div key={p} className="bg-white border border-slate-200 rounded-xl p-3">
              <div className="font-black text-2xl text-emerald-600">{n}</div>
              <div className="text-[10px] uppercase font-black tracking-widest text-slate-500 mt-1 leading-tight">
                {p}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Entry form */}
      {showForm && (
        <div className="bg-white border-2 border-[#00C4CC] rounded-2xl p-4 md:p-5 mb-5 shadow-lg" data-testid="pest-form">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <Field label="Date">
              <input type="date" value={form.entry_date} onChange={e => setF("entry_date", e.target.value)}
                data-testid="pest-date" className={inputCls}/>
            </Field>
            <Field label="Time">
              <input type="time" value={form.entry_time} onChange={e => setF("entry_time", e.target.value)}
                data-testid="pest-time" className={inputCls}/>
            </Field>
            <Field label="Applicator Name *">
              <input value={form.applicator_name} onChange={e => setF("applicator_name", e.target.value)}
                placeholder="Andrew L." data-testid="pest-applicator" className={inputCls}/>
            </Field>
            <Field label="Cert #">
              <input value={form.applicator_cert_number} onChange={e => setF("applicator_cert_number", e.target.value)}
                placeholder="BU-12345" data-testid="pest-cert" className={inputCls}/>
            </Field>
            <Field label="Cert Category">
              <select value={form.applicator_cert_category} onChange={e => setF("applicator_cert_category", e.target.value)}
                data-testid="pest-cert-cat" className={inputCls}>
                {(data.cert_categories || []).map(c => <option key={c}>{c}</option>)}
              </select>
            </Field>
            <Field label="Site Address *">
              <input value={form.site_address} onChange={e => setF("site_address", e.target.value)}
                placeholder="123 Main St" data-testid="pest-site" className={inputCls}/>
            </Field>
            {/* Common Protocols — deterministic, zero-LLM shortcut for the
                15 most-common Northeast pest calls. Picks the protocol that
                matches the currently-selected Target Pest, or all when none. */}
            <div className="col-span-full" data-testid="pest-common-fixes">
              <div className="text-[10px] uppercase font-black tracking-widest text-emerald-700 mb-1.5 inline-flex items-center gap-1">
                <Wrench weight="fill" size={11}/> Common Protocols — one-tap fill
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-1.5 max-h-40 overflow-y-auto">
                {protocolsForPest(form.target_pest, 15).map(p => (
                  <button key={p.id} type="button"
                    data-testid={`pest-quickfix-${p.id}`}
                    onClick={() => {
                      const prod = PESTICIDES.find(x => x.name === p.recommended_product);
                      // Map protocol categories → backend display values.
                      const PEST_DISPLAY = {
                        cockroaches: "German Cockroach", ants: "Ants (general)",
                        rodents: "Rats", termites: "Termites (subterranean)",
                        bedbugs: "Bed Bugs", wasps: "Wasps / Hornets",
                        spiders: "Spiders", fleas: "Fleas", mosquitoes: "Mosquitoes",
                      };
                      setForm(prev => ({
                        ...prev,
                        target_pest: PEST_DISPLAY[p.target_pest] || prev.target_pest,
                        product_name: p.recommended_product,
                        epa_reg_number: prod?.epa_reg || prev.epa_reg_number,
                        application_type: p.application_type || prev.application_type,
                        notes: prev.notes || (p.notes ? `${p.label} · ${p.notes}` : p.label),
                        follow_up_days: p.follow_up_days || null,
                      }));
                      toast.success(`Protocol loaded · ${p.recommended_product}${p.follow_up_days?` · follow-up +${p.follow_up_days}d`:""}`);
                    }}
                    className="text-left px-2.5 py-2 border-2 border-emerald-200 hover:border-emerald-500 bg-emerald-50 hover:bg-emerald-100 rounded-lg leading-tight">
                    <div className="text-[11px] font-bold text-emerald-900">{p.label}</div>
                    <div className="text-[10px] text-emerald-700 mt-0.5 truncate">{p.recommended_product}</div>
                  </button>
                ))}
              </div>
              <div className="text-[10px] text-slate-400 mt-1.5 italic">
                Tapping a protocol fills product + EPA # + application type. You still enter quantity + address.
              </div>
            </div>

            {/* Link to recurring account — enables auto-schedule follow-up */}
            <div className="col-span-full" data-testid="pest-link-account-wrap">
              <label className="text-[10px] uppercase font-black tracking-widest text-slate-500 mb-1 block">
                Link to Recurring Account {autoLinked && <span className="text-emerald-600">· Auto-linked from Start Job</span>}
              </label>
              <div className="flex items-center gap-2 flex-wrap">
                <select value={linkAccountId} onChange={e => setLinkAccountId(e.target.value)}
                  data-testid="pest-link-account"
                  className="flex-1 min-w-[200px] h-11 px-3 border-2 border-slate-200 focus:border-[#00C4CC] outline-none rounded-lg font-medium">
                  <option value="">Not linked (optional)</option>
                  {accounts.map(a => (
                    <option key={a.id} value={a.id}>{a.account_name} · {a.cadence}</option>
                  ))}
                </select>
                {form.follow_up_days && (
                  <span className="px-2 py-1 rounded bg-emerald-100 text-emerald-800 text-[10px] font-black uppercase tracking-widest inline-flex items-center gap-1" data-testid="pest-followup-chip">
                    +{form.follow_up_days}d follow-up
                  </span>
                )}
              </div>
              {linkAccountId && form.follow_up_days && (
                <div className="text-[10px] text-emerald-700 mt-1 font-medium">
                  ✓ On save, this account's next visit auto-books to today + {form.follow_up_days} days.
                </div>
              )}
            </div>
            <Field label="Product / Brand *">
              <PesticideAutocomplete
                value={form.product_name}
                onChange={v => setF("product_name", v)}
                onSelect={p => {
                  // Auto-fill EPA reg # + best-guess target pest so the tech
                  // doesn't retype anything that lives on the label.
                  setForm(prev => ({
                    ...prev,
                    product_name: p.name,
                    epa_reg_number: p.epa_reg,
                    target_pest: prev.target_pest && prev.target_pest !== "other"
                      ? prev.target_pest
                      : (p.targets?.[0] || prev.target_pest),
                  }));
                  toast.success(`${p.name} · EPA ${p.epa_reg}`);
                }}
                testid="pest-product"
              />
            </Field>
            <Field label="EPA Reg # *">
              <input value={form.epa_reg_number} onChange={e => setF("epa_reg_number", e.target.value)}
                placeholder="100-1484" data-testid="pest-epa" className={inputCls}/>
            </Field>
            <Field label="Target Pest *">
              <select value={form.target_pest} onChange={e => setF("target_pest", e.target.value)}
                data-testid="pest-target" className={inputCls}>
                {(data.target_pests || []).map(c => <option key={c}>{c}</option>)}
              </select>
            </Field>
            <Field label="Application Type *">
              <select value={form.application_type} onChange={e => setF("application_type", e.target.value)}
                data-testid="pest-apptype" className={inputCls}>
                {(data.application_types || []).map(c => <option key={c} value={c}>{APP_TYPE_LABELS[c] || c}</option>)}
              </select>
            </Field>
            <Field label="Quantity *">
              <input value={form.quantity} onChange={e => setF("quantity", e.target.value)}
                placeholder="4 oz or 0.5 gal" data-testid="pest-qty" className={inputCls}/>
            </Field>
            <Field label="Dilution">
              <input value={form.dilution} onChange={e => setF("dilution", e.target.value)}
                placeholder="1:64 or 0.05% AI" data-testid="pest-dilution" className={inputCls}/>
            </Field>
            <Field label="Area Treated">
              <input value={form.area_treated} onChange={e => setF("area_treated", e.target.value)}
                placeholder="Kitchen · behind fridge" data-testid="pest-area" className={inputCls}/>
            </Field>
            <Field label="Restricted Entry (hrs)">
              <input type="number" step="0.25" value={form.reentry_hours} onChange={e => setF("reentry_hours", e.target.value)}
                placeholder="4" data-testid="pest-rei" className={inputCls}/>
            </Field>
            <Field label="Temp (°F)">
              <input type="number" step="0.1" value={form.temperature_f} onChange={e => setF("temperature_f", e.target.value)}
                placeholder="72" data-testid="pest-temp" className={inputCls}/>
            </Field>
            <Field label="Wind (mph)">
              <input type="number" step="0.1" value={form.wind_mph} onChange={e => setF("wind_mph", e.target.value)}
                placeholder="5" data-testid="pest-wind" className={inputCls}/>
            </Field>
            <label className="block md:col-span-2">
              <div className="text-[10px] uppercase font-black tracking-widest text-slate-500 mb-1">Notes</div>
              <textarea value={form.notes} onChange={e => setF("notes", e.target.value)} rows={2}
                placeholder="Sealed exit points at baseboard. Follow-up in 14 days."
                data-testid="pest-notes"
                className="w-full px-3 py-2 border-2 border-slate-200 focus:border-[#00C4CC] outline-none rounded-lg font-medium"/>
            </label>
          </div>
          <div className="mt-4 flex items-center gap-3">
            <button onClick={save} disabled={saving} data-testid="pest-save-btn"
              className="h-11 px-6 bg-[#00C4CC] hover:bg-[#00a9b1] text-white font-black uppercase tracking-widest text-xs rounded-xl inline-flex items-center gap-2 disabled:opacity-50 shadow">
              <FloppyDisk weight="bold" size={16}/> {saving ? "Saving…" : "Save Application"}
            </button>
            <div className="text-[11px] text-slate-500">EPA reg # must match the product label — inspectors check.</div>
          </div>
        </div>
      )}

      {/* Ledger */}
      <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden">
        {data.entries.length === 0 ? (
          <div className="p-8 text-center text-slate-400 text-sm" data-testid="pest-empty">
            No applications logged yet. Tap <b>New Application</b> to record your first treatment.
          </div>
        ) : data.entries.map(e => (
          <div key={e.id} data-testid={`pest-row-${e.id}`}
            className="grid grid-cols-2 md:grid-cols-12 gap-2 px-4 py-3 border-b border-slate-100 items-center text-sm hover:bg-slate-50 transition-colors">
            <div className="md:col-span-2">
              <div className="font-mono text-xs">{e.entry_date}{e.entry_time ? ` · ${e.entry_time}` : ""}</div>
              <div className="text-[10px] text-slate-500">{e.applicator_name}{e.applicator_cert_number ? ` · ${e.applicator_cert_number}` : ""}</div>
            </div>
            <div className="md:col-span-3">
              <div className="font-bold text-sm truncate">{e.product_name}</div>
              <div className="text-[10px] font-mono text-slate-500">EPA {e.epa_reg_number}</div>
            </div>
            <div className="md:col-span-2">
              <span className="inline-block px-2 py-0.5 rounded text-[10px] font-black uppercase tracking-widest bg-emerald-100 text-emerald-800">
                {e.target_pest}
              </span>
            </div>
            <div className="md:col-span-1 text-xs">{APP_TYPE_LABELS[e.application_type] || e.application_type}</div>
            <div className="md:col-span-1 font-black text-sm">{e.quantity}</div>
            <div className="md:col-span-2 flex items-center gap-2 justify-between">
              <div className="text-xs text-slate-600 truncate">{e.site_address}</div>
              <button onClick={() => remove(e.id)} data-testid={`pest-delete-${e.id}`}
                className="text-slate-400 hover:text-red-600 p-1 flex-shrink-0">
                <Trash size={14}/>
              </button>
            </div>
          </div>
        ))}
      </div>

      {/* Photo-to-Pest ID Result Modal */}
      {(idResult || idLoading) && (
        <div className="fixed inset-0 z-40 bg-black/50 flex items-center justify-center p-4"
          onClick={() => !idLoading && (setIdResult(null), setIdPreview(null))} data-testid="pest-id-modal">
          <div onClick={e => e.stopPropagation()}
            className="bg-white rounded-2xl max-w-md w-full max-h-[90vh] overflow-y-auto shadow-2xl">
            <div className="p-5 border-b border-slate-200 flex items-center justify-between sticky top-0 bg-white z-10">
              <div className="flex items-center gap-2">
                <Camera weight="fill" size={22} className="text-emerald-500"/>
                <div className="font-display font-black text-xl">Photo Identification</div>
              </div>
              {!idLoading && (
                <button onClick={() => { setIdResult(null); setIdPreview(null); }}
                  className="text-slate-400 hover:text-slate-800" data-testid="pest-id-close">
                  <X size={20}/>
                </button>
              )}
            </div>
            <div className="p-5">
              {idPreview && (
                <img src={idPreview} alt="Pest photo"
                  className="w-full max-h-56 object-contain rounded-lg mb-4 bg-slate-50"/>
              )}
              {idLoading ? (
                <div className="text-center py-6">
                  <div className="text-sm font-bold text-slate-600 mb-2">Analyzing photo…</div>
                  <div className="text-[11px] text-slate-500">Claude Vision is looking at the image</div>
                </div>
              ) : idResult && (
                <div data-testid="pest-id-result">
                  {idResult.not_a_pest ? (
                    <div className="p-3 rounded-lg bg-amber-50 border-l-4 border-amber-500 text-amber-800 text-sm">
                      AI didn't see a clear pest or sign in this photo. Try a closer or better-lit shot.
                    </div>
                  ) : (
                    <>
                      <div className="text-[10px] uppercase font-black tracking-widest text-emerald-600 mb-1">
                        Identified · Confidence: {idResult.confidence}
                      </div>
                      <div className="font-display font-black text-2xl leading-tight" data-testid="pest-id-name">
                        {idResult.pest_specific || idResult.pest}
                      </div>
                      {idResult.pest_specific && idResult.pest !== "Other" && (
                        <div className="text-xs text-slate-500 mt-1">
                          Maps to: <span className="font-bold">{idResult.pest}</span>
                        </div>
                      )}
                      <div className="mt-3 flex items-center gap-2 flex-wrap">
                        {idResult.sign_type && (
                          <span className="text-[10px] font-black uppercase tracking-widest bg-slate-100 text-slate-700 px-2 py-1 rounded">
                            {idResult.sign_type.replace("_", " ")}
                          </span>
                        )}
                        {idResult.urgency && (
                          <span className={`text-[10px] font-black uppercase tracking-widest px-2 py-1 rounded ${
                            idResult.urgency === "urgent" ? "bg-red-100 text-red-800" :
                            idResult.urgency === "low" ? "bg-slate-100 text-slate-700" :
                            "bg-cyan-100 text-cyan-800"
                          }`}>
                            {idResult.urgency}
                          </span>
                        )}
                      </div>
                      {(idResult.observed || []).length > 0 && (
                        <div className="mt-4">
                          <div className="text-[10px] uppercase font-black tracking-widest text-slate-500 mb-1">What AI Saw</div>
                          <ul className="list-disc pl-5 text-sm space-y-1">
                            {idResult.observed.map((s, i) => <li key={i}>{s}</li>)}
                          </ul>
                        </div>
                      )}
                      {idResult.next_step && (
                        <div className="mt-4 p-3 rounded-lg bg-fuchsia-50 border-l-4 border-fuchsia-500 text-fuchsia-900 text-sm">
                          <b>Next step:</b> {idResult.next_step}
                        </div>
                      )}
                      <button onClick={useIdForTreatment} data-testid="pest-id-plan-btn"
                        className="mt-5 w-full h-12 bg-[#00C4CC] hover:bg-[#00a9b1] text-white font-black uppercase tracking-widest text-xs rounded-xl inline-flex items-center justify-center gap-2 shadow">
                        <Sparkle weight="fill" size={14}/> Build Treatment Plan
                      </button>
                    </>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* AI Advisor Modal */}
      {aiOpen && (
        <div className="fixed inset-0 z-40 bg-black/50 flex items-center justify-center p-4"
          onClick={() => !aiLoading && setAiOpen(false)} data-testid="pest-ai-modal">
          <div onClick={e => e.stopPropagation()}
            className="bg-white rounded-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto shadow-2xl">
            <div className="p-5 border-b border-slate-200 flex items-center justify-between sticky top-0 bg-white z-10">
              <div className="flex items-center gap-2">
                <Sparkle weight="fill" size={22} className="text-fuchsia-500"/>
                <div className="font-display font-black text-xl">Ask AI: What Should I Use?</div>
              </div>
              <button onClick={() => setAiOpen(false)} className="text-slate-400 hover:text-slate-800" data-testid="pest-ai-close">
                <X size={20}/>
              </button>
            </div>
            <div className="p-5">
              {!aiAdvice ? (
                <>
                  <div className="text-xs text-slate-500 mb-3">
                    Tell the AI what you're seeing — it'll suggest a product plan and placement. You still verify the label before you spray.
                  </div>
                  <div className="space-y-3">
                    <Field label="Target Pest">
                      <select value={aiInput.target_pest} onChange={e => setAiInput(p => ({ ...p, target_pest: e.target.value }))}
                        data-testid="pest-ai-pest" className={inputCls}>
                        {(data.target_pests || []).map(c => <option key={c}>{c}</option>)}
                      </select>
                    </Field>
                    <Field label="Site Type">
                      <select value={aiInput.site_type} onChange={e => setAiInput(p => ({ ...p, site_type: e.target.value }))}
                        data-testid="pest-ai-site" className={inputCls}>
                        <option value="residential">Residential Home / Apartment</option>
                        <option value="commercial_kitchen">Commercial Kitchen / Restaurant</option>
                        <option value="warehouse">Warehouse / Storage</option>
                        <option value="apartment">Multi-unit Apartment Building</option>
                        <option value="school">School / Daycare</option>
                        <option value="healthcare">Healthcare / Medical</option>
                        <option value="outdoor">Outdoor / Grounds</option>
                      </select>
                    </Field>
                    <Field label="What are you seeing? (droppings, live activity, entry points, etc.)">
                      <textarea value={aiInput.situation} rows={3}
                        onChange={e => setAiInput(p => ({ ...p, situation: e.target.value }))}
                        placeholder="Lots of activity behind the fridge, droppings on baseboards, gap under sink"
                        data-testid="pest-ai-situation"
                        className="w-full px-3 py-2 border-2 border-slate-200 focus:border-[#00C4CC] outline-none rounded-lg font-medium"/>
                    </Field>
                    <label className="flex items-center gap-2 cursor-pointer" data-testid="pest-ai-kids">
                      <input type="checkbox" checked={aiInput.has_kids_or_pets}
                        onChange={e => setAiInput(p => ({ ...p, has_kids_or_pets: e.target.checked }))}
                        className="h-5 w-5"/>
                      <span className="text-sm font-bold">Kids or pets present at this site</span>
                    </label>
                  </div>
                  <button onClick={askAI} disabled={aiLoading} data-testid="pest-ai-submit"
                    className="mt-4 w-full h-12 bg-fuchsia-600 hover:bg-fuchsia-700 text-white font-black uppercase tracking-widest text-xs rounded-xl inline-flex items-center justify-center gap-2 disabled:opacity-50 shadow">
                    <Sparkle weight="fill" size={16}/> {aiLoading ? "Thinking…" : "Get Recommendation"}
                  </button>
                </>
              ) : (
                <div data-testid="pest-ai-advice">
                  <div className="text-[10px] uppercase font-black tracking-widest text-fuchsia-600 mb-1">
                    AI Plan · Confidence: {aiAdvice.confidence || "medium"}
                  </div>
                  <div className="font-bold text-base leading-snug mb-4">{aiAdvice.summary}</div>

                  {(aiAdvice.products || []).length > 0 && (
                    <Section title="Suggested Products">
                      {aiAdvice.products.map((p, i) => (
                        <div key={i} className="border-l-4 border-emerald-500 pl-3 py-1 mb-2">
                          <div className="font-bold text-sm">{p.name}
                            <span className="ml-2 text-[10px] uppercase font-black tracking-widest text-emerald-700 bg-emerald-100 px-1.5 py-0.5 rounded">
                              {p.class}
                            </span>
                          </div>
                          <div className="text-xs text-slate-600">{p.why}</div>
                          {p.epa_reg && <div className="text-[10px] font-mono text-slate-500 mt-0.5">EPA {p.epa_reg}</div>}
                        </div>
                      ))}
                    </Section>
                  )}

                  {aiAdvice.application_type && (
                    <Section title="Application Type">
                      <span className="inline-block px-2 py-1 rounded text-xs font-black uppercase bg-cyan-100 text-cyan-800">
                        {APP_TYPE_LABELS[aiAdvice.application_type] || aiAdvice.application_type}
                      </span>
                    </Section>
                  )}

                  {(aiAdvice.placement || []).length > 0 && (
                    <Section title="Where to Place / Apply">
                      <ul className="list-disc pl-5 text-sm space-y-1">
                        {aiAdvice.placement.map((s, i) => <li key={i}>{s}</li>)}
                      </ul>
                    </Section>
                  )}

                  {(aiAdvice.avoid || []).length > 0 && (
                    <Section title="Do NOT" tone="red">
                      <ul className="list-disc pl-5 text-sm space-y-1 text-red-700">
                        {aiAdvice.avoid.map((s, i) => <li key={i}>{s}</li>)}
                      </ul>
                    </Section>
                  )}

                  {(aiAdvice.safety || []).length > 0 && (
                    <Section title="Safety" tone="amber">
                      <ul className="list-disc pl-5 text-sm space-y-1 text-amber-800">
                        {aiAdvice.safety.map((s, i) => <li key={i}>{s}</li>)}
                      </ul>
                    </Section>
                  )}

                  {aiAdvice.follow_up_days && (
                    <div className="text-xs text-slate-600 mt-3">
                      📅 Suggested follow-up: <b>{aiAdvice.follow_up_days} days</b>
                    </div>
                  )}

                  <div className="mt-5 flex flex-col gap-2">
                    <button onClick={applyAdvice} data-testid="pest-ai-apply"
                      className="w-full h-11 bg-[#00C4CC] hover:bg-[#00a9b1] text-white font-black uppercase tracking-widest text-xs rounded-xl inline-flex items-center justify-center gap-2 shadow">
                      Apply to New Application Form
                    </button>
                    <button onClick={() => setAiAdvice(null)} data-testid="pest-ai-reset"
                      className="w-full h-10 border-2 border-slate-300 hover:bg-slate-100 text-slate-700 font-black uppercase tracking-widest text-xs rounded-xl">
                      Ask Again
                    </button>
                  </div>

                  <div className="mt-4 text-[10px] text-slate-500 leading-relaxed">
                    ⚠ AI is a helper, not a substitute for the product label.
                    You are still legally responsible for reading the label of
                    whatever you actually apply. Rates, sites, and safety rules
                    on the label are the law.
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
    </AdminShell>
  );
}

function Section({ title, tone = "default", children }) {
  const toneCls = tone === "red" ? "text-red-700" : tone === "amber" ? "text-amber-700" : "text-slate-500";
  return (
    <div className="mb-3">
      <div className={`text-[10px] uppercase font-black tracking-widest mb-1 ${toneCls}`}>{title}</div>
      {children}
    </div>
  );
}

const inputCls = "w-full h-11 px-3 border-2 border-slate-200 focus:border-[#00C4CC] outline-none rounded-lg font-medium";

function Field({ label, children }) {
  return (
    <label className="block">
      <div className="text-[10px] uppercase font-black tracking-widest text-slate-500 mb-1">{label}</div>
      {children}
    </label>
  );
}
